<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row dense>
      <v-col
        v-if="label"
        class="labeled-input"
        cols="12"
      >
        <label :for="inputId">
          {{ label }}
          <RequiredIndicator v-if="mandatory" />

          <slot name="label-icon">
            <HelpIcon
              :document="helpDocument"
              :on-help-click="openHelpFlyout"
            />
            <v-tooltip
              v-if="tooltipText"
              :location="tooltipLocation"
            >
              <template #activator="tooltipOptions">
                <span>
                  <v-icon
                    v-bind="tooltipOptions.props"
                    :color="tooltipColor"
                    :icon="tooltipIcon"
                    class="d-inline ps-1 pt-0 pb-1 material-icons-outlined"
                  />
                </span>
              </template>

              <span>
                {{ $bct(tooltipText) }}
              </span>
            </v-tooltip>
          </slot>
        </label>
      </v-col>

      <v-col
        v-else-if="title || subtitle"
        cols="12"
      >
        <div
          :class="[titleClasses, bold ? 'fs-16 fw-600' : 'fs-16 fw-500']"
          class="c-black"
        >
          {{ $bct(title) }}
          <RequiredIndicator v-if="mandatory" />
        </div>

        <div
          v-if="subtitle"
          class="fs-14 c-light-black mt-1 text-pre-wrap"
        >
          {{ $bct(subtitle) }}
        </div>
      </v-col>

      <template v-if="description">
        <v-col
          class="fs-16 c-light-black my-1"
          cols="12"
        >
          <span>
            {{ $bct(description) }}
          </span>
        </v-col>
      </template>

      <v-col>
        <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -->
        <v-select
          @blur="handleBlur"
          @update:model-value="handleUpdateModelValue"
          :id="inputId"
          :append-inner-icon="appendIcon"
          :aria-label="ariaLabel"
          :chips="chips"
          :clearable="clearable"
          :closable-chips="deletableChips"
          :density="dense || veryDense ? 'compact' : undefined"
          :disabled="locked"
          :item-props="itemProps"
          :item-title="itemTitle"
          :item-value="itemValue"
          :items="items"
          :menu-props="{ 'data-testid': menuTestId }"
          :model-value="localValue"
          :no-data-text="noDataText"
          data-cy="labeled-select"
          data-testid="labeled-select"
          variant="filled"
          hide-details
          tile
          v-bind="filteredAttrs"
        />
      </v-col>
    </v-row>
  </v-col>
</template>

<script setup>
import _ from 'lodash';
import { onUpdated } from 'vue';

import attrsOmitDataPrefix from '@/shared/utils/filteredAttrs';
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const attrs = useAttrs();
const $bct = inject('translator');

defineEmits(useLabeledField.emits);

const props = defineProps({
  chips: Boolean,
  clearable: {
    type: Boolean,
    default: true,
  },
  deletableChips: Boolean,
  id: {
    type: String,
    default: undefined,
  },
  items: {
    type: Array,
    default: () => [],
  },
  itemProps: Boolean,
  menuTestId: {
    type: String,
    default: undefined,
  },
  noDataText: {
    type: String,
    default: undefined,
  },
  itemTitle: {
    type: String,
    default: 'text',
  },
  itemValue: {
    type: String,
    default: 'value',
  },
  subtitle: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  titleClasses: {
    type: [String, Array, Object],
    default: undefined,
  },
  tooltipColor: {
    type: String,
    default: null,
  },
  tooltipIcon: {
    type: String,
    default: 'info',
  },
  tooltipLocation: {
    type: String,
    default: 'top',
  },
  tooltipText: {
    type: String,
    default: null,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const localValue = defineModel({ type: [String, Number, Array] });

const inputId = ref(props.id || _.uniqueId('labeled-select-'));

const filteredAttrs = ref(attrsOmitDataPrefix(attrs));

const { appendIcon, handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, ariaLabel, helpDocument, label, locked, openHelpFlyout } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

// attrs is not a reactive object, so we update the filteredAttrs ref on every DOM tree update
onUpdated(() => {
  filteredAttrs.value = attrsOmitDataPrefix(attrs);
});
</script>
